import { type TextareaHTMLAttributes, forwardRef, useEffect, useRef } from 'react';

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  autoResize?: boolean;
}

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ label, error, autoResize = true, className = '', id, onChange, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const internalRef = useRef<HTMLTextAreaElement | null>(null);

    const setRef = (el: HTMLTextAreaElement | null) => {
      internalRef.current = el;
      if (typeof ref === 'function') {
        ref(el);
      } else if (ref) {
        ref.current = el;
      }
    };

    // Auto-resize the textarea based on content
    useEffect(() => {
      if (autoResize && internalRef.current) {
        const el = internalRef.current;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
      }
    }, [autoResize, props.value]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (autoResize) {
        const el = e.target;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
      }
      onChange?.(e);
    };

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="mb-1 block text-sm font-medium text-neutral-700">
            {label}
          </label>
        )}
        <textarea
          ref={setRef}
          id={inputId}
          className={`
            w-full rounded-lg border px-3 py-2 text-sm
            transition-colors duration-150 resize-none
            placeholder:text-neutral-400 text-neutral-300
            focus:outline-none focus:ring-2 focus:ring-offset-0
            ${
              error
                ? 'border-error-500 focus:border-error-500 focus:ring-error-500'
                : 'border-neutral-300 focus:border-primary-500 focus:ring-primary-500'
            }
            disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-500
            ${className}
          `}
          rows={1}
          aria-invalid={!!error}
          onChange={handleChange}
          {...props}
        />
        {error && (
          <p className="mt-1 text-sm text-error-600" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  },
);

TextArea.displayName = 'TextArea';

export default TextArea;
